import { FC, useCallback, useEffect, useState } from 'react';
import { Link, useRouteMatch, useHistory } from 'react-router-dom';
import { useFilePicker } from 'react-filepicker-hook';
import toast from 'react-hot-toast';
import useSwr from 'swr';

import { CreateUserInput, Tier } from '@sdk/API';
import { getUserByUsername, updateUser } from '@sdk/managers/UsersManager';
import { getImageUrl, uploadImage } from '@utils/image';
import { useAuth } from '@hooks/Auth';

import ButtonMighty from '@components/elements/ButtonMighty';
import { EditIcon } from '@components/elements/Icons';
import BlockImagePicker from '@components/elements/BlockImagePicker';

import './AccountPage.css';
import JoinCompanyButton from '@components/modules/Account/JoinCompanyButton';
import LeaveCompanyButton from '@components/modules/Account/LeaveCompanyButton';

const updateUserImage = async (userId: string, file: File) => {
  const imageS3 = await uploadImage(file, userId + '_' + new Date().getTime());

  await updateUser(userId, {
    image: imageS3,
  });

  return imageS3;
};

type User = CreateUserInput;

const SALARY_RANGE = {
  '1': { value: '_1', label: 'Below $11,999' },
  '2': { value: '_2', label: '$12,000 - $29,999' },
  '3': { value: '_3', label: '$30,000 - $49,999' },
  '4': { value: '_4', label: '$50,000 - $79,999' },
  '5': { value: '_5', label: '$80,000 - $119,999' },
  '6': { value: '_6', label: '$120,000 and above' },
};

const getUserProfile = async (
  _query: string,
  username: string
): Promise<User> => {
  if (!username) throw new Error('You are not logged in.');

  const res: any = await getUserByUsername(
    [
      {
        items: [
          'id',
          'firstName',
          'lastName',
          'gender',
          'email',
          'phone',
          'yearOfBirth',
          'education',
          'postalCode',
          'cryptowallet',
          'salaryRange',
          'image { key bucket region }',
          'organisation { name department jobTitle }',
        ],
      },
    ],
    username
  );

  const {
    data: {
      result: { items: users },
    },
  } = res;

  // Select first user that isnt null
  const userDetails: User[] = users.filter((u) => !!u);
  if (!userDetails || userDetails.length < 1)
    throw new Error('Failed to retrieve user details.');

  return userDetails[0];
};

const AccountPage: FC = () => {
  const history = useHistory<any>();
  const { url } = useRouteMatch();
  const { user: authUser, dbUser, logout, refetch, company } = useAuth();

  const [image, setImage] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const { files, setFiles } = useFilePicker();

  useEffect(() => {
    refetch();
  }, [refetch]);

  const { data: user, error } = useSwr<User>(
    () => ['query: getUserProfile', authUser.username],
    getUserProfile,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  // Set user image
  useEffect(() => {
    if (user) {
      const setUserImage = async () => {
        try {
          setImage(user.image ? await getImageUrl(user.image) : undefined);
        } catch (err: any) {
          console.error('parse user details', err);
          toast.error(err.message);
        }
      };
      setUserImage();
    }
  }, [user, setImage]);

  const onLogout = useCallback(async () => {
    setLoading(true);

    await toast.promise(logout(), {
      loading: 'Signing out...',
      success: 'Successfully signed out',
      error: (err) => `${err.message}`,
    });

    await history.replace('/');
    setTimeout(() => {
      window.location.reload();
    });
    setLoading(false);
  }, [logout, setLoading, history]);

  // Handle change user image
  useEffect(() => {
    if (files.length > 0) {
      const update = async () => {
        if (!dbUser?.id) toast.error('You are not logged in.');
        else {
          const imageS3 = await toast.promise(
            updateUserImage(dbUser.id, files[0]),
            {
              loading: 'Uploading profile picture...',
              success: 'Successfully uploaded profile picture',
              error: (err) => {
                console.error(err);
                if (err.message) return err.message;

                if (err.errors?.length > 0) {
                  const firstErr = err.errors[0];
                  if (firstErr.errorType !== 'DynamoDB:DynamoDbException') {
                    return firstErr.message;
                  }
                }
                return 'Failed to update profile picture';
              },
            }
          );
          // Prevent reuploading image
          setFiles([]);
          setImage(await getImageUrl(imageS3));
        }
      };
      update();
    }
  }, [files, dbUser, setFiles]);

  let content;
  let leftContent;
  if (user) {
    const salaryRange = Object.keys(SALARY_RANGE).find((key) => {
      const range = SALARY_RANGE[key];
      return range.value === user.salaryRange;
    });
    const sections = [
      {
        columns: [
          {
            properties: [
              {
                label: 'Email',
                value: user.email,
              },
              {
                label: 'Gender',
                value: user.gender,
              },
              {
                label: 'Username',
                value: authUser?.username,
              },
              {
                label: 'Year of birth',
                value: user.yearOfBirth,
              },
            ],
          },
        ],
      },
      {
        columns: [
          {
            properties: [
              {
                label: 'Phone number',
                value: user.phone,
              },
              {
                label: 'Postal Code',
                value: user.postalCode,
              },
              {
                label: 'Organisation',
                value: user.organisation?.name,
              },
              {
                label: 'Job title',
                value: user.organisation?.jobTitle,
              },
              {
                label: 'Education',
                value: user.education,
              },
              {
                label: 'Annual Salary Range',
                value: salaryRange
                  ? SALARY_RANGE[salaryRange]?.label || salaryRange
                  : user.salaryRange,
              },
              {
                label: 'Crypto Wallet (Eg Metamask, Coinbase Wallet...)',
                value: user.cryptowallet,
              },
              {
                label: 'Department',
                value: user.organisation?.department,
              },
            ],
          },
        ],
      },
    ];
    const actions = (
      <div>
        <div className="space-y-4">
          <div>
            <Link to={`${url}/plan`}>
              <ButtonMighty
                disabled={false}
                loading={false}
                color="#4400FF"
                text="Manage Plan"
                borderColor="#ff1dfc"
                margin="0"
                textColor="#fff"
              />
            </Link>
          </div>
          <div>
            <Link to={`${url}/edit`}>
              <ButtonMighty
                color="#ff1dfc"
                text="Manage Account"
                borderColor="#FFFF00"
                margin="0"
                textColor="#fff"
              />
            </Link>
            {/* Handle no company setup dont show */}
            {!company && dbUser?.tier !== Tier.ENTERPRISE && (
              <div>
                <JoinCompanyButton />
              </div>
            )}
            {company && dbUser?.tier !== Tier.ENTERPRISE && (
              <div>
                <LeaveCompanyButton />
              </div>
            )}
          </div>
          <div>
            <ButtonMighty
              disabled={loading}
              loading={loading}
              color="#000"
              text="Logout"
              borderColor="#F17373"
              margin="0"
              textColor="#fff"
              onClick={onLogout}
            />
          </div>
        </div>
      </div>
    );
    leftContent = (
      <div className="mt-4 ml-4">
        <div className="flex-col hidden p-4 text-white lg:flex bg-gray-darkest">
          <h2 className="text-xl">Actions</h2>
          {actions}
        </div>
      </div>
    );
    content = (
      <>
        <div>
          {company?.name && (
            <div className="flex">
              <div className="px-4 py-2 mb-1 text-white uppercase text-md bg-blue">
                Company - {company.name} ({company.role})
              </div>
            </div>
          )}
          {sections.map((section, i) => (
            <div key={`account-section-${i}`}>
              <div>
                {section.columns.map((column, j) => (
                  <div key={`account-section-${i}-col-${j}`}>
                    {column.properties.map(({ label, value }, k) => (
                      <div
                        key={`account-section-${i}-col-${j}-${label}-${j}`}
                        className="py-4"
                      >
                        <h3 className="text-2xl text-magenta">{label}</h3>
                        <p className="text-lg break-all">{value || '-'}</p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {i < sections.length - 1 && (
                <div className="h-px my-4 bg-white" />
              )}
            </div>
          ))}
        </div>

        <div className="lg:hidden">{actions}</div>
      </>
    );
  }
  return (
    <div className="bg-green">
      <div className="relative">
        <div className="relative bg-gray-darkest">
          <div className="container mx-auto">
            {user && (
              <div>
                <h1 className="absolute z-10 w-full px-4 overflow-hidden text-4xl text-white align-bottom overflow-ellipsis bottom-4 max-h-1/2">
                  <span className="align-text-bottom">
                    {user && `${user.firstName} \n${user.lastName}`}
                  </span>
                </h1>
              </div>
            )}
          </div>
          <div className="container relative mx-auto">
            <div
              role="button"
              className="absolute right-0 z-10 p-1 mr-4 bg-white rounded-lg cursor-pointer pointer-events-none mt-14 text-gray-darkest text-opacity-70"
            >
              <EditIcon />
            </div>
          </div>

          <div className="pointer-events-none fadeBottom" />
          <div className="container mx-auto overflow-hidden h-96">
            <BlockImagePicker
              imageSrc={image}
              onChange={(files) => {
                if (files.length > 0) {
                  const update = async () => {
                    if (!dbUser?.id) toast.error('You are not logged in.');
                    else {
                      const imageS3 = await toast.promise(
                        updateUserImage(dbUser.id, files[0]),
                        {
                          loading: 'Uploading profile picture...',
                          success: 'Successfully uploaded profile picture',
                          error: (err) => {
                            console.error(err);
                            if (err.message) return err.message;

                            if (err.errors?.length > 0) {
                              const firstErr = err.errors[0];
                              if (
                                firstErr.errorType !==
                                'DynamoDB:DynamoDbException'
                              ) {
                                return firstErr.message;
                              }
                            }
                            return 'Failed to update profile picture';
                          },
                        }
                      );
                      // Prevent reuploading image
                      setFiles([]);
                      setImage(await getImageUrl(imageS3));
                    }
                  };
                  update();
                }
              }}
              length={1}
            />
          </div>
        </div>
      </div>
      <div className="container mx-auto">
        <div className="flex flex-col gap-4 mx-auto lg:container lg:flex-row">
          <div className="w-full lg:min-h-screen lg:sticky lg:top-0 lg:w-80">
            {leftContent}
          </div>
          <div className="flex flex-1">
            <div className="flex-1 pb-24">
              <div className="p-4 account-background">
                <div
                  className="p-8 text-white bg-gray-darkest"
                  style={!user ? { minHeight: 700 } : {}}
                >
                  {content}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
